import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BellSimpleRinging, CheckCircle, PaperPlaneTilt, WarningCircle } from "@phosphor-icons/react";
import { isPushSupported, currentPermission, enablePush, disablePush } from "@/lib/push";

/**
 * Card that lets a user manage push notifications on the current device.
 * Shows subscription count (across all their devices), permission state,
 * and offers Enable / Disable / Send test buttons.
 */
export default function PushSettingsCard() {
  const [status, setStatus] = useState({ configured: false, subscription_count: 0 });
  const [perm, setPerm] = useState(currentPermission());
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const supported = isPushSupported();

  const refresh = async () => {
    try {
      const r = await api.get("/push/status");
      setStatus(r.data);
    } catch { /* endpoint not deployed yet */ }
    setPerm(currentPermission());
  };
  useEffect(() => { refresh(); }, []);

  const onEnable = async () => {
    setBusy(true);
    const r = await enablePush();
    setBusy(false);
    if (r.ok) {
      toast.success("Notifications activées sur cet appareil");
      refresh();
    } else if (r.reason === "denied") {
      toast.error("Autorisation refusée — activez-les dans les réglages du navigateur");
      setPerm("denied");
    } else if (r.reason === "unsupported") {
      toast.error("Ce navigateur ne supporte pas les notifications push");
    } else {
      toast.error("Impossible d'activer — réessayez plus tard");
    }
  };

  const onDisable = async () => {
    if (!window.confirm("Désactiver les notifications push sur cet appareil ?")) return;
    setBusy(true);
    await disablePush();
    setBusy(false);
    toast.success("Notifications désactivées");
    refresh();
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/push/test");
      if (r.data?.sent > 0) {
        toast.success(`Push envoyé sur ${r.data.sent} appareil${r.data.sent > 1 ? "s" : ""} — vérifiez la bannière système`);
      } else if (r.data?.skipped) {
        toast.error("Push non configuré côté serveur (VAPID absent)");
      } else {
        toast.error("Aucun appareil abonné — activez d'abord les notifications");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex items-center gap-3" data-testid="push-card-unsupported">
        <WarningCircle size={22} className="text-amber-400 shrink-0" weight="duotone" />
        <div className="text-xs text-white/60">Ce navigateur ne supporte pas les notifications push. Sur iPhone/iPad, ajoutez d&apos;abord BKflow à l&apos;écran d&apos;accueil.</div>
      </div>
    );
  }

  const enabledHere = perm === "granted" && status.subscription_count > 0;

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 space-y-3" data-testid="push-settings-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#005CFF]/20 border border-[#005CFF]/30 flex items-center justify-center shrink-0">
          <BellSimpleRinging size={18} weight="duotone" className="text-[#7CB2FF]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold flex items-center gap-2">
            Notifications push
            {enabledHere && <CheckCircle size={14} weight="fill" className="text-emerald-400" />}
          </div>
          <div className="text-[11px] text-white/50 leading-tight mt-0.5">
            {perm === "granted"
              ? `Actif sur ${status.subscription_count} appareil${status.subscription_count > 1 ? "s" : ""}`
              : perm === "denied"
                ? "Refusé — modifiez la permission dans les réglages navigateur"
                : "Recevez les alertes app fermée (nouvelle intervention, réassignation…)"}
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {perm !== "granted" ? (
          <Button
            onClick={onEnable}
            disabled={busy || perm === "denied"}
            className="flex-1 h-10 bg-[#005CFF] hover:bg-[#0047CC] rounded-xl text-sm"
            data-testid="push-enable-in-settings"
          >
            {busy ? "…" : "Activer sur cet appareil"}
          </Button>
        ) : (
          <>
            <Button
              onClick={onTest}
              disabled={testing || status.subscription_count === 0}
              className="flex-1 h-10 bg-emerald-500 hover:bg-emerald-600 rounded-xl text-sm"
              data-testid="push-test-btn"
            >
              <PaperPlaneTilt size={14} weight="fill" /> {testing ? "Envoi…" : "Envoyer un test"}
            </Button>
            <Button
              onClick={onDisable}
              disabled={busy}
              variant="ghost"
              className="h-10 border border-white/10 text-red-400 hover:bg-red-500/10 rounded-xl text-sm"
              data-testid="push-disable-btn"
            >
              Désactiver
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
